import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const PLATFORM_ROLE_GROUPS = ["SUPER_ADMIN", "SUPPORT", "SALES", "FINANCE", "CS", "DEVELOPER", "QA"];

export default function PlatformUsers() {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("all");
  const [filter, setFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/admin/users/${id}/action`, { action }); toast.success(`User ${action.toLowerCase()}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const platformUsers = users.filter((u) => u.role === "SUPER_ADMIN" || PLATFORM_ROLE_GROUPS.some((g) => u.role.startsWith(g)));
  const visible = (tab === "all" ? platformUsers : platformUsers.filter((u) => u.role === tab))
    .filter((u) => !filter || u.email.toLowerCase().includes(filter.toLowerCase()) || (u.full_name || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-users">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Platform users</h1>
        <p className="text-muted-foreground">Super Admins, Support, Sales, Finance, CS, Developers, QA — everyone who works at Projexino HQ.</p>
      </div>
      <div className="flex items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {PLATFORM_ROLE_GROUPS.map((r) => <TabsTrigger key={r} value={r}>{r}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Input placeholder="Search…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" />
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>MFA</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((u) => (
              <TableRow key={u.id} data-testid={`platform-user-${u.id}`}>
                <TableCell className="font-semibold">{u.full_name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="font-mono text-xs">{u.role}</TableCell>
                <TableCell>{u.mfa_enabled ? <Badge>On</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{u.account_status}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  {u.role !== "SUPER_ADMIN" && (
                    <>
                      {u.account_status !== "APPROVED" && <Button size="sm" onClick={() => act(u.id, "APPROVE")} data-testid={`approve-${u.id}`}>Approve</Button>}
                      {u.account_status === "APPROVED" && <Button size="sm" variant="outline" onClick={() => act(u.id, "SUSPEND")} data-testid={`suspend-${u.id}`}>Suspend</Button>}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No platform users in this segment yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
